import { AllowedMaterialSpec, isPortOfLoadingRequired, type SimpleTradeCompanyListItem, SiteMaterialList } from 'api';
import {
  AllowedMaterialSpecAutocomplete,
  DateField,
  IncotermSelectField,
  MaterialGradeAutocomplete,
  PortAutocomplete,
  PriceAsCurrencyOrIndex,
  SiteMaterialWithProductAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  TradeCompanyLeadAutocomplete,
  useFormContext,
  useWatch,
} from 'form';
import { useState } from 'react';
import { ActionRow, Button, Stack } from 'ui';

import { getCreateSiteMaterialUrl, NoSiteMaterialsOption } from '~/site-materials';

import { CompanySourceEnum, type CompanySourceType } from '../../enums';
import { SupplySignalFormValues } from '../../schema/SupplySignalSchema/SupplySignalSchema';
import { CompanySourceToggleButton } from '../CompanySourceToggleButton/CompanySourceToggleButton';

export const SupplySignalFormSection = () => {
  const [selectedCompanyType, setSelectedCompanyType] = useState<CompanySourceType>(CompanySourceEnum.GROUND_CONTROL);
  const form = useFormContext<SupplySignalFormValues>();
  const { setValue } = form;
  const [incoterm, tradeCompanyId, siteMaterialId, portOfLoadingId] = useWatch({
    name: ['incoterm', 'trade_company_id', 'site_material_id', 'port_of_loading_id'],
  });
  const isGroundControlSelected = selectedCompanyType === CompanySourceEnum.GROUND_CONTROL;

  const onTradeCompanyChange = (tradeCompany: SimpleTradeCompanyListItem) => {
    setValue('site_material_id', undefined);
    if (tradeCompany.currency) {
      setValue('currency', tradeCompany.currency);
    }
    if (tradeCompany.preferred_port_of_loading_id) {
      setValue('port_of_loading_id', tradeCompany.preferred_port_of_loading_id);
    } else {
      setValue('port_of_loading_id', undefined);
    }
  };

  const onTradeCompanyTypeChange = (tradeCompanyType: CompanySourceType) => {
    setValue('trade_company_id', undefined);
    setValue('trade_company_lead_id', undefined);
    setValue('site_material_id', undefined);
    setValue('material_spec_id', undefined);
    setValue('material_grade_id', undefined);
    setSelectedCompanyType(tradeCompanyType);
  };

  const onMaterialSpecChange = (materialSpec: AllowedMaterialSpec) => {
    if (materialSpec?.default_payload_size) {
      setValue('tonnes_per_load', Number(materialSpec.default_payload_size));
    }
  };

  const onSiteMaterialChange = (siteMaterial: SiteMaterialList) => {
    if (siteMaterial?.default_payload_size) {
      setValue('tonnes_per_load', Number(siteMaterial.default_payload_size));
      return;
    }
    if (siteMaterial?.material_spec.default_payload_size) {
      setValue('tonnes_per_load', Number(siteMaterial.material_spec.default_payload_size));
    }
  };

  return (
    <>
      <CompanySourceToggleButton
        onTradeCompanyTypeChange={onTradeCompanyTypeChange}
        tradeCompanyType={selectedCompanyType}
      />
      {isGroundControlSelected ? (
        <>
          <TradeCompanyAutocomplete
            name="trade_company_id"
            label="Select seller from Ground Control"
            onlySellers={true}
            onSelectOption={(tradeCompany) => onTradeCompanyChange(tradeCompany)}
          />
          <Stack direction="column" alignItems="flex-end" justifyContent="center" gap={1}>
            <ActionRow>
              <Button
                variant="text"
                component="a"
                disabled={!siteMaterialId}
                target="_blank"
                href={getCreateSiteMaterialUrl(tradeCompanyId)}
                aria-label="Add new site material"
              >
                Add new site material
              </Button>
              <Button
                variant="text"
                component="a"
                disabled={!siteMaterialId}
                target="_blank"
                href={`/quality/${siteMaterialId}/`}
                aria-label="Link to quality report"
              >
                Link to quality report
              </Button>
            </ActionRow>
            <SiteMaterialWithProductAutocomplete
              name="site_material_id"
              label="Site Material"
              disabled={!tradeCompanyId}
              tradeCompanyId={tradeCompanyId}
              noOptionsText={<NoSiteMaterialsOption tradeCompanyId={tradeCompanyId} />}
              onSelectOption={onSiteMaterialChange}
              fullWidth
            />
          </Stack>
        </>
      ) : (
        <>
          <TradeCompanyLeadAutocomplete name="trade_company_lead_id" label="Select seller from Hubspot" />
          <AllowedMaterialSpecAutocomplete
            label="Material"
            name="material_spec_id"
            onSelectOption={onMaterialSpecChange}
          />
          <MaterialGradeAutocomplete label="Material grade" name="material_grade_id" />
        </>
      )}
      <PriceAsCurrencyOrIndex />
      <IncotermSelectField fullWidth />
      {isPortOfLoadingRequired(incoterm) && (
        <PortAutocomplete name="port_of_loading_id" label="Select port of loading" defaultValue={portOfLoadingId} />
      )}
      <TextField label="Tonnes" name="tonnes" type="number" required data-testid="tonnes" />
      <TextField label="Tonnes per load" name="tonnes_per_load" type="number" required />
      <DateField label="Sale target date" name="sale_target_date" />
      <DateField label="Last collection date" name="last_collection_date" required />
      <TextField multiline label="Standup notes" name="notes" aria-label="Notes" />
    </>
  );
};
